import { useEffect, useState, useCallback, useRef } from "react";
import { Search, Download, Trash2, Pencil, X, BookMarked, FolderPlus, Eye } from "lucide-react";
import { toast } from "sonner";
import { webApi, errText, fmtBytes, fileUrl } from "@/lib/webApi";
import { PageHead, Empty, ghost, primary } from "@/pages/web/WebApp";
import { Card, Eyebrow, Pill } from "@/pages/web/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FILTERS = [["all", "All"], ["unsorted", "Unsorted"], ["library", "Library"], ["orders", "Order copies"], ["drafts", "Drafts"]];

function Panel({ f, matters, onClose, onChange }) {
  const [target, setTarget] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const confirmRef = useRef(null);
  const names = Object.fromEntries(matters.map((m) => [m.id, m.name]));
  const previewable = /^(application\/pdf|image\/)/.test(f.mime || "");
  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); onChange(); } catch (e) { toast.error(errText(e)); }
  };
  const rename = (e) => {
    e.preventDefault();
    const name = draft;
    setRenameOpen(false);
    if (name) act(() => webApi.patch(`/files/${f.id}`, { name }), "Renamed");
  };
  const del = () => {
    act(() => webApi.delete(`/files/${f.id}`), "Deleted").then(onClose);
  };
  return (
    <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l border-[--w-border] bg-[--w-card] text-[--w-text] shadow-xl" data-testid="file-panel">
      <div className="flex items-start justify-between gap-3 border-b border-[--w-border] p-4">
        <div className="min-w-0">
          <p className="truncate font-medium text-[--w-text]" data-testid="file-panel-name">{f.name}</p>
          <p className="text-xs text-[--w-text-3] tabular-nums">{fmtBytes(f.size)} · {f.date}</p>
        </div>
        <button onClick={onClose} data-testid="file-panel-close" className="text-[--w-text-3] hover:text-[--w-text]"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 overflow-auto">
        {previewable ? <iframe title="preview" src={fileUrl(f.id)} className="h-[55vh] w-full bg-white" data-testid="file-preview" /> :
          <p className="p-6 text-sm text-[--w-text-3]">No preview for this file type. Download it to open.</p>}
        <div className="space-y-4 p-4">
          {f.gist && <p className="text-sm text-[--w-text-2]">{f.gist}</p>}
          <div className="flex flex-wrap gap-2">
            <a href={fileUrl(f.id, true)} className={ghost} data-testid="file-download-button"><Download className="h-4 w-4" /> Download</a>
            <button onClick={() => { setDraft(f.name); setRenameOpen(true); }} className={ghost} data-testid="file-rename-button"><Pencil className="h-4 w-4" /> Rename</button>
            <button onClick={() => act(() => webApi.patch(`/files/${f.id}`, { library: !f.library }), f.library ? "Removed from Library" : "Added to Library")} className={ghost} data-testid="file-library-toggle">
              <BookMarked className="h-4 w-4" /> {f.library ? "Remove from Library" : "Add to Library"}
            </button>
            <button onClick={() => setDelOpen(true)} className={`${ghost} text-red-700`} data-testid="file-delete-button"><Trash2 className="h-4 w-4" /> Delete</button>
          </div>
          <div>
            <Eyebrow className="mb-2">Filed in</Eyebrow>
            <div className="flex flex-wrap gap-2">
              {(f.matters || []).length === 0 && <span className="text-sm text-[--w-text-3]">Unsorted</span>}
              {(f.matters || []).map((mid) => (
                <Pill key={mid} className="gap-1 py-1">
                  {names[mid] || "Matter"}
                  <button onClick={() => act(() => webApi.delete(`/files/${f.id}/matters/${mid}`), "Removed from matter")} data-testid={`file-unfile-${mid}`} className="text-[--w-text-3] hover:text-red-700"><X className="h-3 w-3" /></button>
                </Pill>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="file-matter-select" className="flex-1"><SelectValue placeholder="File in a matter..." /></SelectTrigger>
                <SelectContent>
                  {matters.filter((m) => !(f.matters || []).includes(m.id)).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <button disabled={!target} onClick={() => act(() => webApi.post(`/files/${f.id}/matters`, { matter_id: target }), "Filed").then(() => setTarget(""))} className={primary} data-testid="file-matter-add"><FolderPlus className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="theme-warm">
          <DialogHeader><DialogTitle>Rename file</DialogTitle></DialogHeader>
          <form onSubmit={rename} className="space-y-4">
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              className="w-full rounded-md border border-[--w-border] bg-[--w-card] px-3 py-2 text-sm text-[--w-text] outline-none focus:border-[--w-accent]" />
            <DialogFooter>
              <button type="button" onClick={() => setRenameOpen(false)} className={ghost}>Cancel</button>
              <button type="submit" className={primary}>Rename</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="theme-warm" onOpenAutoFocus={(e) => { e.preventDefault(); confirmRef.current?.focus(); }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file</AlertDialogTitle>
            <AlertDialogDescription>Delete this file from every matter and the Library?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction ref={confirmRef} onClick={del} className="bg-[--w-danger] text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}

export function FileTable({ rows, onChange }) {
  const [open, setOpen] = useState(null);
  const [matters, setMatters] = useState([]);
  useEffect(() => {
    webApi.get("/matters").then((r) => setMatters(r.data)).catch(() => {});
  }, [rows]);
  const cur = open && rows.find((r) => r.id === open);
  return (
    <>
      <Card className="max-h-[70vh] overflow-auto" data-testid="file-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[--w-card-2] text-left text-[11px] uppercase tracking-wide text-[--w-text-3]">
            <tr><th className="px-4 py-2">Name</th><th className="hidden px-4 py-2 sm:table-cell">Date</th><th className="hidden px-4 py-2 sm:table-cell">Size</th><th className="px-2 py-2" /></tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} onClick={() => setOpen(f.id)} data-testid={`file-row-${f.id}`} className="group h-11 cursor-pointer border-t border-[--w-border] transition-colors duration-150 hover:bg-[--w-card-2]">
                <td className="px-4 py-2.5 text-sm font-medium text-[--w-text]">{f.name}{f.library && <Pill tone="ok" className="ml-2 px-2 text-[10px]">Library</Pill>}</td>
                <td className="hidden px-4 py-2.5 text-[13px] text-[--w-text-2] tabular-nums sm:table-cell">{f.date}</td>
                <td className="hidden px-4 py-2.5 text-[13px] text-[--w-text-2] tabular-nums sm:table-cell">{fmtBytes(f.size)}</td>
                <td className="px-2"><Eye className="h-4 w-4 text-[--w-text-3] opacity-0 group-hover:opacity-100" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {cur && <Panel f={cur} matters={matters} onClose={() => setOpen(null)} onChange={() => onChange && onChange()} />}
    </>
  );
}

export function FilesView({ library }) {
  const [filter, setFilter] = useState(library ? "library" : "all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => webApi.get("/files", { params: { filter: library ? "library" : filter, q } }).then((r) => setRows(r.data)).catch(() => setRows([])), [filter, q, library]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    const k = (e) => { if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); document.getElementById("file-search")?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  return (
    <div data-testid={library ? "web-library" : "web-files"}>
      <PageHead title={library ? "Library" : "Files"} sub={library ? "Judgments and precedents that belong to no matter" : undefined}>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-[--w-text-3]" />
          <input id="file-search" data-testid="file-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={library ? "Search files · press /" : "Search by name, party or case number · press /"}
            className="w-64 rounded-md border border-[--w-border] bg-[--w-card] py-2 pl-9 pr-3 text-sm text-[--w-text] outline-none focus:border-[--w-accent] sm:w-80" />
        </div>
      </PageHead>
      {!library && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`file-filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${filter === k ? "bg-[--w-text] text-white" : "border border-[--w-border] bg-[--w-card] text-[--w-text-2] hover:text-[--w-text]"}`}>{label}</button>
          ))}
        </div>
      )}
      {rows && rows.length === 0 ? <Empty>{library ? "Your Library is empty. Forward a judgment on WhatsApp and reply SAVE TO LIBRARY." : "No files here yet. Forward one on WhatsApp or upload here."}</Empty>
        : <FileTable rows={rows || []} onChange={load} />}
    </div>
  );
}
